import { useMutation, useQueryClient } from "@tanstack/react-query";

import { withJWTClient } from "../connectClient";

import { API_BASE_URL, connectorsKeys } from "@/api/connectors/index";
import {
  ByocEventSchema,
  ByocEventsResponseSchema,
  type ByocEvent,
} from "@/types/connectors";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// GET /{siteId}/byoc/events

export async function getByocEvents(siteId: string): Promise<ByocEvent[]> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/byoc/events`),
  );
  const parseResult = ByocEventsResponseSchema.safeParse(response.data);
  if (!parseResult.success) {
    throw new Error("BYOC events response schema mismatch.");
  }
  return parseResult.data.events;
}

// POST /{siteId}/byoc/events

export async function createByocEvent(
  siteId: string,
  payload: { name: string },
): Promise<ByocEvent> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(`${API_BASE_URL}/${siteId}/byoc/events`, payload),
  );
  const parseResult = ByocEventSchema.safeParse(response.data);
  if (!parseResult.success) {
    throw new Error("BYOC event create response schema mismatch.");
  }
  return parseResult.data;
}

// PATCH /{siteId}/byoc/events/{eventId}

export type UpdateByocEventPayload = {
  enabled: boolean;
};

export async function updateByocEvent(
  siteId: string,
  eventId: string,
  payload: UpdateByocEventPayload,
): Promise<void> {
  await withJWTClient((client) =>
    client.patch(`${API_BASE_URL}/${siteId}/byoc/events/${eventId}`, payload),
  );
}

// DELETE /{siteId}/byoc/events/{eventId}

export async function deleteByocEvent(
  siteId: string,
  eventId: string,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete(`${API_BASE_URL}/${siteId}/byoc/events/${eventId}`),
  );
}

export const byocEventsKeys = {
  list: (siteId: string) =>
    [...connectorsKeys.all(siteId), "byocEvents"] as const,
};

export const byocEventsQueryOptions = (siteId: string) => ({
  queryKey: byocEventsKeys.list(siteId),
  queryFn: () => getByocEvents(siteId),
  enabled: !!siteId,
});

export function useCreateByocEvent(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation<ByocEvent, Error, { name: string }>({
    mutationFn: (payload) => createByocEvent(siteId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: byocEventsKeys.list(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to add the event.");
    },
  });
}

export function useUpdateByocEvent(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    UpdateByocEventPayload & { eventId: string },
    { previousEvents?: ByocEvent[] }
  >({
    mutationFn: ({ eventId, ...payload }) =>
      updateByocEvent(siteId, eventId, payload),
    // Optimistic update so the enabled switch flips immediately.
    onMutate: async ({ eventId, ...payload }) => {
      await queryClient.cancelQueries({
        queryKey: byocEventsKeys.list(siteId),
      });
      const previousEvents = queryClient.getQueryData<ByocEvent[]>(
        byocEventsKeys.list(siteId),
      );
      if (previousEvents) {
        queryClient.setQueryData<ByocEvent[]>(
          byocEventsKeys.list(siteId),
          previousEvents.map((event) =>
            event.id === eventId ? { ...event, ...payload } : event,
          ),
        );
      }
      return { previousEvents };
    },
    onError: (error, _variables, context) => {
      if (context?.previousEvents) {
        queryClient.setQueryData(
          byocEventsKeys.list(siteId),
          context.previousEvents,
        );
      }
      enqueueErrorSnackbar(error, "Failed to update the event.");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: byocEventsKeys.list(siteId),
      });
    },
  });
}

export function useDeleteByocEvent(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (eventId) => deleteByocEvent(siteId, eventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: byocEventsKeys.list(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete the event.");
    },
  });
}
